import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { MailrailBaseEmailCampaign } from '@feathr/blackbox';
import { CampaignState } from '@feathr/blackbox';
import {
  CardContent,
  CardHeader,
  CardV2,
  DatePicker,
  Fieldset,
  Form,
  Radios,
} from '@feathr/components';
import ConsentCapture from '@feathr/extender/components/ConsentCapture';
import { useUser } from '@feathr/extender/state';
import { flattenError, moment, TimeFormat } from '@feathr/hooks';
import type { TValidateGrouped } from '@feathr/rachis';

import MailrailCampaignSegments from '../../MailrailCampaignSegments';
import MailrailCampaignSendingOptions from '../../MailrailCampaignSendingOptions';
import PinpointEmailCampaignAudienceSummary from '../../PinpointEmailCampaignAudienceSummary';

import * as styles from './MailrailCampaignStepScheduleSend.css';

interface IProps {
  campaign: MailrailBaseEmailCampaign;
  disabled: boolean;
  stepNumber: number;
}

interface IErrors extends TValidateGrouped {
  bulk_send_enabled?: string[];
  bulk_send_timing?: string[];
  'consent.has_consent'?: string[];
  date_end?: string[];
  date_start?: string[];
  repeat_allowed?: string[];
  repeat_cooldown_seconds?: string[];
  segments_excluded?: string[];
  segments_included?: string[];
}

export function validateStepScheduleSend(campaign: MailrailBaseEmailCampaign): IErrors {
  const validationFields = [
    'consent.has_consent',
    'date_start',
    'segments_excluded',
    'segments_included',
  ];
  if (campaign.isMailrailAutoSend) {
    validationFields.push(
      'bulk_send_enabled',
      'bulk_send_timing',
      'date_end',
      'repeat_allowed',
      'repeat_cooldown_seconds',
    );
  }
  return campaign.validate(validationFields, false, 'grouped').errors;
}

function MailrailCampaignStepScheduleSend({
  campaign,
  disabled,
  stepNumber,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const user = useUser();
  const stats = campaign.get('stats_cache');

  const userTimezone = user.get('timezone');
  const isTimeSubtypeAutosend = campaign.isMailrailAutoSend && campaign.get('subtype') === 'time';
  const dateStart = campaign.get('date_start');
  const dateEnd = campaign.get('date_end');
  const sendSchedule = dateStart ? 'later' : 'now';
  const startMoment = moment.utc(dateStart);
  const startMomentLocalized = userTimezone
    ? startMoment.clone().tz(userTimezone)
    : startMoment.clone().local();
  const endMoment = dateEnd ? moment.utc(dateEnd) : undefined;
  const endMomentLocalized = userTimezone
    ? endMoment?.clone().tz(userTimezone)
    : endMoment?.clone().local();

  function onSendtimeChange(newValue?: string): void {
    if (newValue === 'now') {
      campaign.set({ date_start: undefined });
    }

    if (newValue === 'later') {
      const newStart = moment.utc().add(15, 'minutes');
      const roundedMinutes = Math.ceil(newStart.minute() / 5) * 5;
      newStart.minute(roundedMinutes).second(0);
      campaign.set({ date_start: newStart.format(TimeFormat.isoDateTime) });
    }
  }

  function onStartDateChange(newTimestamp: string | undefined): void {
    if (isTimeSubtypeAutosend) {
      // The date picker should only change the date component of date_start. The time component should be kept unchanged.
      // This will be the campaign's first send time. It will send at the same time every day through its end date.
      const newDate = moment.tz(newTimestamp, userTimezone);
      newDate.set({
        hour: startMomentLocalized.hour(),
        minute: startMomentLocalized.minute(),
        second: startMomentLocalized.second(),
      });
      campaign.set({ date_start: newDate.utc().format(TimeFormat.isoDateTime) });
    } else {
      campaign.set({ date_start: newTimestamp });
    }
  }

  function onEndDateChange(newTimestamp: string | undefined): void {
    if (isTimeSubtypeAutosend) {
      // The end date should be set to the user's chosen date component combined with the time component of the start date.
      // This will be the campaign's final send time.
      const newDate = moment.tz(newTimestamp, userTimezone);
      newDate.set({
        hour: startMomentLocalized.hour(),
        minute: startMomentLocalized.minute(),
        second: startMomentLocalized.second(),
      });
      campaign.set({ date_end: newDate.utc().format(TimeFormat.isoDateTime) });
    } else {
      campaign.set({ date_end: newTimestamp });
    }
  }

  function handleChangeTimeOfDay(newTimestamp?: string): void {
    // Updates the time component of date_start and date_end without changing their date components
    const newTime = moment.utc(newTimestamp);
    const start = moment.utc(dateStart);
    const end = moment.utc(dateEnd);
    start.set({ hour: newTime.hour(), minute: newTime.minute(), second: 0 });
    end.set({ hour: newTime.hour(), minute: newTime.minute(), second: 0 });
    const formattedStart = start.format(TimeFormat.isoDateTime);
    const formattedEnd = end.format(TimeFormat.isoDateTime);
    campaign.set({ date_start: formattedStart, date_end: formattedEnd });
  }

  function handleChangeConsent(newValue = false): void {
    if (newValue) {
      campaign.set({
        consent: {
          user: user!.id,
          has_consent: true,
          date_consented: moment.utc().format(TimeFormat.isoDateTime),
        },
      });
    } else {
      campaign.set({
        consent: {
          has_consent: false,
        },
      });
    }
  }

  function getMinStartDate(): Date {
    // When a time subtype autosend's send time is earlier than now + 15min, the minDate of the picker should be tomorrow because the campaign can't actually send anything today
    if (isTimeSubtypeAutosend) {
      const now = moment.utc();
      const sendTimeToday = now.clone().set({
        hour: startMoment.hour(),
        minute: startMoment.minute(),
        second: startMoment.second(),
      });
      if (sendTimeToday.isBefore(now.clone().add(15, 'minutes'))) {
        const nowLocal = userTimezone ? now.tz(userTimezone) : now.local();
        return nowLocal.add(1, 'day').startOf('day').toDate();
      }
    }
    const nowLocal = userTimezone ? moment.tz(userTimezone) : moment().local();
    return nowLocal.startOf('day').toDate();
  }

  function getMinStartTime(): Date {
    const nowLocal = userTimezone ? moment().tz(userTimezone) : moment().local();
    if (startMomentLocalized.isBefore(nowLocal.clone().startOf('day'))) {
      return nowLocal.endOf('day').toDate();
    }
    if (startMomentLocalized.clone().startOf('day').isAfter(nowLocal)) {
      return nowLocal.startOf('day').toDate();
    }
    return nowLocal.add(15, 'minutes').toDate();
  }

  function getMinEndTime(): Date {
    const nowLocal = userTimezone ? moment().tz(userTimezone) : moment().local();
    if (
      endMomentLocalized &&
      startMomentLocalized.clone().add(1, 'day').isAfter(endMomentLocalized.clone().endOf('day'))
    ) {
      return nowLocal.endOf('day').toDate();
    }
    if (
      endMomentLocalized &&
      startMomentLocalized.clone().add(1, 'day').isAfter(endMomentLocalized.clone().startOf('day'))
    ) {
      return startMomentLocalized.toDate();
    }
    return nowLocal.startOf('day').toDate();
  }

  function getScheduleCardHelpText(): string {
    if (campaign.isMailrailAutoSend) {
      return t(
        'People who meet the triggers of this campaign during the specified timeframe will receive your message. The maximum time window is one year.',
      );
    }
    return t("Choose whether you'd like to send your email now or at a later date.");
  }

  function getStartDateHelpText(): string {
    if (isTimeSubtypeAutosend) {
      return t(
        'The date on which Feathr should start sending emails to contacts who match the trigger.',
      );
    }
    const formattedStartTime = startMoment.format(TimeFormat.pickerDateTimeZone);
    if (campaign.isMailrailAutoSend) {
      return t(
        'Choose the time that Feathr should start sending emails to contacts who perform the trigger. {{date}}',
        { date: formattedStartTime },
      );
    }
    return t('The date and time your email will be sent. {{date}}', { date: formattedStartTime });
  }

  function getEndDateHelpText(): string {
    if (isTimeSubtypeAutosend) {
      return t(
        'The last date on which Feathr should send emails to contacts who match the trigger.',
      );
    }
    const formattedEndTime = endMoment ? endMoment.format(TimeFormat.pickerDateTimeZone) : '';
    return t(
      'Choose the time that Feathr should stop sending emails to contacts who perform the trigger. {{date}}',
      { date: formattedEndTime },
    );
  }

  const validationErrors = validateStepScheduleSend(campaign);

  const formDescription = campaign.isMailrailAutoSend
    ? t(
        'Optional: Narrow your target audience using group(s). If you specify one or more included groups, then contacts who perform the trigger will only be emailed if they also belong to an included group. Any contact in an excluded group will not be sent this email, even if they perform the trigger.',
      )
    : t(
        "Choose or create group(s) of people that will receive this email. Anyone in an included group will receive your email as long as they aren't in any excluded group.",
      );

  return (
    <Form description={formDescription} label={t('Edit Campaign: Audience')} width={'wide'}>
      <MailrailCampaignSegments campaign={campaign} disabled={disabled} step={stepNumber} />
      <MailrailCampaignSendingOptions
        campaign={campaign}
        disabled={disabled}
        validationErrors={validationErrors}
      />
      <CardV2 width={'full'}>
        <CardHeader description={getScheduleCardHelpText()} title={t('Schedule & Send')} />
        <CardContent>
          <Fieldset className={styles.schedule}>
            {!isTimeSubtypeAutosend && (
              <Radios
                dataName={'send_schedule'}
                disabled={disabled}
                label={campaign.isMailrailAutoSend ? t('Choose start time') : t('Choose send time')}
                onChange={onSendtimeChange}
                options={[
                  { id: 'now', name: t('Now') },
                  { id: 'later', name: t('Later') },
                ]}
                value={sendSchedule}
              />
            )}
            {isTimeSubtypeAutosend && (
              <DatePicker
                autoComplete={'off'}
                dateFormat={'h:mm aa'}
                disabled={disabled}
                helpText={t(
                  'Choose the time of day when emails should be sent to contacts matching the date trigger(s).',
                )}
                label={t('Time of day')}
                name={'time_of_day'}
                onDateStrChange={handleChangeTimeOfDay}
                selected={startMomentLocalized.toDate()}
                showTimeSelect={true}
                showTimeSelectOnly={true}
                suffix={moment.tz(userTimezone || moment.tz.guess()).zoneAbbr()}
                timeIntervals={5}
                validationError={flattenError(validationErrors.date_start)}
                wrapperClassName={styles.timeOfDaySelect}
              />
            )}
            {sendSchedule === 'later' && (
              <DatePicker
                autoComplete={'off'}
                dateFormat={isTimeSubtypeAutosend ? 'MMM d, yyyy' : 'MMM d, yyyy h:mm aa'}
                disabled={disabled}
                helpPlacement={'top'}
                helpText={getStartDateHelpText()}
                label={campaign.isMailrailAutoSend ? t('Start date') : t('Send date')}
                maxTime={moment().local().clone().endOf('day').toDate()}
                minDate={getMinStartDate()}
                minTime={getMinStartTime()}
                name={'date_start'}
                onDateStrChange={onStartDateChange}
                selected={startMomentLocalized.toDate()}
                showTimeSelect={!isTimeSubtypeAutosend}
                suffix={moment.tz(userTimezone || moment.tz.guess()).zoneAbbr()}
                timeIntervals={5}
                validationError={flattenError(validationErrors.date_start)}
              />
            )}
            {campaign.isMailrailAutoSend && (
              <DatePicker
                autoComplete={'off'}
                dateFormat={isTimeSubtypeAutosend ? 'MMM d, yyyy' : 'MMM d, yyyy h:mm aa'}
                disabled={disabled}
                helpPlacement={'top'}
                helpText={getEndDateHelpText()}
                key={dateStart}
                label={t('End date')}
                maxDate={moment.utc(dateStart).local().add(1, 'year').toDate()}
                maxTime={moment().local().endOf('day').toDate()}
                minDate={moment.utc(dateStart).local().add(1, 'day').toDate()}
                minTime={getMinEndTime()}
                name={'date_end'}
                onDateStrChange={onEndDateChange}
                selected={endMomentLocalized?.toDate()}
                showTimeSelect={!isTimeSubtypeAutosend}
                suffix={moment.tz(userTimezone || moment.tz.guess()).zoneAbbr()}
                timeIntervals={5}
                validationError={flattenError(validationErrors?.date_end)}
              />
            )}
          </Fieldset>
        </CardContent>
      </CardV2>
      <CardV2 width={'full'}>
        <CardHeader title={t('Verify & Publish')} />
        <CardContent>
          <ConsentCapture
            alertDescription={t(
              'I understand that if my campaigns exhibit abnormally high bounce or complaint rates, it may result in my account being placed under temporary review or suspension.',
            )}
            alertTitle={t(
              'I attest that I am sending this email to recipients who have signed up and specifically requested to receive email about this topic from me or my organization.',
            )}
            disabled={disabled}
            label={t(
              'Please type "AGREE" in the box below to verify the above statement (case-sensitive).',
            )}
            onChange={handleChangeConsent}
            value={campaign.get('consent', { has_consent: false }).has_consent}
          />
        </CardContent>
      </CardV2>
      {!!stats?.num_targeted &&
        [CampaignState.Published, CampaignState.Stopped].includes(campaign.get('state')) &&
        campaign.isAfterDateStart && <PinpointEmailCampaignAudienceSummary stats={stats} />}
    </Form>
  );
}

export default observer(MailrailCampaignStepScheduleSend);
